import { msg } from '@lingui/core/macro';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { isPlaceholderKeyValidFor } from 'modules/manager/domain/template-type';
import { DEFAULT_TEMPLATE_TYPE, isKnownTemplateType, TemplateType } from 'modules/manager/domain/template-type/template-type';
import type { MessageDescriptor } from 'shared/i18n';
import { MediaKind, UNSUPPORTED_MEDIA_KINDS } from './media-kind';

/**
 * Kind → canvas `templateType`. Lives next to `template-type-registry.ts` so the
 * editor can resolve placeholders without a network call. Titles / `supported` /
 * sport coverage on the create form come from `mediaKindCatalog`.
 */
const TEMPLATE_TYPE_BY_KIND: Record<MediaKind, string> = {
  [MediaKind.MATCH_ANNOUNCE]: TemplateType.SCHEDULE,
  [MediaKind.MATCHES_ANNOUNCE]: TemplateType.MULTIPLE_SCHEDULE,
  [MediaKind.MATCH_RESULT]: TemplateType.RESULTS,
  [MediaKind.MATCHES_RESULT]: TemplateType.MULTIPLE_RESULTS,
  [MediaKind.CHAMPIONSHIP_TABLE]: TemplateType.TOURNAMENT_TABLE,
  [MediaKind.GROUP_TABLE]: TemplateType.TOURNAMENT_TABLE,
  [MediaKind.TOP_SCORER]: TemplateType.TOP_PLAYERS_BY_INDICATORS,
  [MediaKind.TOUR_SCORERS]: TemplateType.TOP_PLAYERS_BY_INDICATORS,
  [MediaKind.PLAYOFF_BRACKET]: TemplateType.PLAYOFF_BRACKET,
  [MediaKind.BIRTHDAY]: TemplateType.BIRTHDAY,
  [MediaKind.TOUR_SYMBOLIC_TEAM]: TemplateType.SYMBOLIC_TEAM_ROUND,
  [MediaKind.CHAMPIONSHIP_SYMBOLIC_TEAM]: TemplateType.SYMBOLIC_TEAM_CHAMPIONSHIP,
  [MediaKind.VIDEO_INTRO]: TemplateType.VIDEO_INTRO,
  [MediaKind.VIDEO_GOAL_PLATE]: TemplateType.VIDEO_GOAL_PLATE,
  [MediaKind.VIDEO_DANGER_PLATE]: TemplateType.VIDEO_DANGER_PLATE,
};

/**
 * Pre-release templates and files without `mediaKind` map through this table.
 * One canvas type can back several kinds; the primary is the one the migration
 * wrote. Types that did not exist before Phase 1 have no primary.
 */
const PRIMARY_KIND_BY_TEMPLATE_TYPE: Partial<Record<string, MediaKind>> = {
  [TemplateType.SCHEDULE]: MediaKind.MATCH_ANNOUNCE,
  [TemplateType.MULTIPLE_SCHEDULE]: MediaKind.MATCHES_ANNOUNCE,
  [TemplateType.RESULTS]: MediaKind.MATCH_RESULT,
  [TemplateType.MULTIPLE_RESULTS]: MediaKind.MATCHES_RESULT,
  [TemplateType.TOURNAMENT_TABLE]: MediaKind.CHAMPIONSHIP_TABLE,
  [TemplateType.TOP_PLAYERS_BY_INDICATORS]: MediaKind.TOP_SCORER,
};

export const MEDIA_KIND_LABELS: Record<MediaKind, MessageDescriptor> = {
  [MediaKind.MATCH_ANNOUNCE]: msg`Анонс матча`,
  [MediaKind.MATCHES_ANNOUNCE]: msg`Анонсы матчей`,
  [MediaKind.MATCH_RESULT]: msg`Результат матча`,
  [MediaKind.MATCHES_RESULT]: msg`Результаты матчей`,
  [MediaKind.CHAMPIONSHIP_TABLE]: msg`Таблица чемпионата`,
  [MediaKind.GROUP_TABLE]: msg`Группа`,
  [MediaKind.TOP_SCORER]: msg`Лучший бомбардир`,
  [MediaKind.TOUR_SCORERS]: msg`Бомбардиры тура`,
  [MediaKind.PLAYOFF_BRACKET]: msg`Плейофф`,
  [MediaKind.BIRTHDAY]: msg`День рождения`,
  [MediaKind.TOUR_SYMBOLIC_TEAM]: msg`Сборная тура`,
  [MediaKind.CHAMPIONSHIP_SYMBOLIC_TEAM]: msg`Сборная чемпионата`,
  [MediaKind.VIDEO_INTRO]: msg`Заставка видео`,
  [MediaKind.VIDEO_GOAL_PLATE]: msg`Плашка: гол`,
  [MediaKind.VIDEO_DANGER_PLATE]: msg`Плашка: опасный момент`,
};

export function templateTypeFor(kind: MediaKind): string {
  return TEMPLATE_TYPE_BY_KIND[kind];
}

export function canvasTemplateTypeFor(kind: MediaKind): TemplateType {
  const type = templateTypeFor(kind);
  return isKnownTemplateType(type) ? type : DEFAULT_TEMPLATE_TYPE;
}

export function primaryKindForTemplateType(templateType: string): MediaKind | null {
  return PRIMARY_KIND_BY_TEMPLATE_TYPE[templateType] ?? null;
}

export function isUnsupportedMediaKind(kind: MediaKind): boolean {
  return (UNSUPPORTED_MEDIA_KINDS as readonly string[]).includes(kind);
}

export type MediaSubjectType = 'match' | 'round' | 'stage' | 'group' | 'championship' | 'player';

const SUBJECT_TYPE_BY_KIND: Record<MediaKind, MediaSubjectType> = {
  [MediaKind.MATCH_ANNOUNCE]: 'match',
  [MediaKind.MATCHES_ANNOUNCE]: 'round',
  [MediaKind.MATCH_RESULT]: 'match',
  [MediaKind.MATCHES_RESULT]: 'round',
  [MediaKind.CHAMPIONSHIP_TABLE]: 'stage',
  [MediaKind.GROUP_TABLE]: 'group',
  [MediaKind.TOP_SCORER]: 'championship',
  [MediaKind.TOUR_SCORERS]: 'round',
  [MediaKind.PLAYOFF_BRACKET]: 'stage',
  [MediaKind.BIRTHDAY]: 'player',
  [MediaKind.TOUR_SYMBOLIC_TEAM]: 'round',
  [MediaKind.CHAMPIONSHIP_SYMBOLIC_TEAM]: 'championship',
  [MediaKind.VIDEO_INTRO]: 'match',
  [MediaKind.VIDEO_GOAL_PLATE]: 'match',
  [MediaKind.VIDEO_DANGER_PLATE]: 'match',
};

export function subjectTypeFor(kind: MediaKind): MediaSubjectType {
  return SUBJECT_TYPE_BY_KIND[kind];
}

export function mediaKindLabel(kind: MediaKind): MessageDescriptor {
  return MEDIA_KIND_LABELS[kind];
}

/** GraphQL `SportType` is uppercase (`FOOTBALL`); the domain enum is lowercase. */
export function toGraphqlSportType(sportType: SportType | string | null | undefined): string | null {
  if (!sportType) return null;
  return String(sportType).toUpperCase();
}

export function collectPlaceholderKeys(objects: unknown): string[] {
  const keys = new Set<string>();
  const walk = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    const rec = value as Record<string, unknown>;
    if (typeof rec.placeholderKey === 'string' && rec.placeholderKey) {
      keys.add(rec.placeholderKey);
    }
    const data = rec.data;
    if (data && typeof data === 'object' && typeof (data as { placeholderKey?: unknown }).placeholderKey === 'string') {
      const key = (data as { placeholderKey: string }).placeholderKey;
      if (key) keys.add(key);
    }
    if (Array.isArray(rec.objects)) rec.objects.forEach(walk);
  };
  walk(objects);
  return Array.from(keys);
}

/**
 * Placeholder keys on the canvas that the new kind's canvas type does not allow.
 * Used when the organizer changes kind on an existing template (US-2).
 */
export function invalidPlaceholderKeysForKind(objects: unknown, kind: MediaKind): string[] {
  const type = canvasTemplateTypeFor(kind);
  return collectPlaceholderKeys(objects).filter((key) => !isPlaceholderKeyValidFor(type, key));
}
